import { apiErrorSchema } from '@mio/shared';

import { env } from '../lib/env';
import { supabase } from '../lib/supabase';

import { ApiRequestError } from './api-error';
import { notifyUsageError } from './usage-events';

import type { ZodType } from 'zod';

const BASE = `${env.VITE_API_URL}/api/v1`;
const REQUEST_TIMEOUT_MS = 30_000;
/** Dispatched when paid access ends mid-session; resource quotas have their own event. */
export const ACCESS_REQUIRED_EVENT = 'mio:access-required';

/** Readable copy for failures the API did not label with its error envelope. */
function statusMessage(status: number) {
  if (status === 401) return 'Your session has expired. Sign in again to continue.';
  if (status === 403) return 'You do not have access to this action.';
  if (status === 404) return 'That item could not be found. Reload and try again.';
  if (status === 409) return 'Something changed since this page loaded. Reload and try again.';
  if (status === 429) return 'Too many requests. Wait a moment and try again.';
  if (status >= 500) return 'Mio is temporarily unavailable. Try again in a moment.';
  return `The request failed (${status}). Try again.`;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

/**
 * Typed fetch against the API. Attaches the Supabase access token, unwraps the
 * shared error envelope, and validates the response against the given contract.
 */
export async function apiRequest<T>(
  path: string,
  schema: ZodType<T>,
  options: RequestOptions = {},
): Promise<T> {
  // Bound the entire request, including session refresh and reading the body.
  // A lost mutation response is uncertain: callers must retain their idempotency
  // key rather than sending a second command automatically.
  const controller = new AbortController();
  const abortFromCaller = () => controller.abort(options.signal?.reason);
  if (options.signal?.aborted) abortFromCaller();
  else options.signal?.addEventListener('abort', abortFromCaller, { once: true });
  const timeout = setTimeout(() => {
    controller.abort(
      new ApiRequestError(
        0,
        'request_timeout',
        'The request took too long. Check your connection and retry.',
      ),
    );
  }, REQUEST_TIMEOUT_MS);
  const { signal } = controller;
  let rejectOnAbort: (() => void) | undefined;
  const aborted = new Promise<never>((_resolve, reject) => {
    rejectOnAbort = () => {
      const reason: unknown = signal.reason;
      reject(
        reason instanceof Error || reason instanceof DOMException
          ? reason
          : new DOMException('Request cancelled', 'AbortError'),
      );
    };
    if (signal.aborted) rejectOnAbort();
    else signal.addEventListener('abort', rejectOnAbort, { once: true });
  });
  const request = async () => {
    signal.throwIfAborted();
    const { data, error } = await supabase.auth.getSession();
    signal.throwIfAborted();
    if (error) throw error;
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (data.session) headers['Authorization'] = `Bearer ${data.session.access_token}`;
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';

    let response: Response;
    try {
      response = await fetch(`${BASE}${path}`, {
        method: options.method ?? 'GET',
        headers,
        body: options.body === undefined ? null : JSON.stringify(options.body),
        signal,
      });
    } catch (error) {
      // A cancelled request reports its own reason; anything else is the
      // browser's opaque "Failed to fetch", which learners cannot act on.
      signal.throwIfAborted();
      throw new ApiRequestError(
        0,
        'network_error',
        'Mio could not be reached. Check your connection and try again.',
        error instanceof Error ? error.message : undefined,
      );
    }

    if (!response.ok) {
      const parsed = apiErrorSchema.safeParse(await response.json().catch(() => null));
      signal.throwIfAborted();
      const usageExceeded = notifyUsageError(parsed.success ? parsed.data.error.code : undefined);
      if (response.status === 402 && !usageExceeded)
        window.dispatchEvent(new Event(ACCESS_REQUIRED_EVENT));
      if (parsed.success) {
        const { code, message, details } = parsed.data.error;
        throw new ApiRequestError(response.status, code, message, details);
      }
      throw new ApiRequestError(response.status, 'unknown', statusMessage(response.status));
    }

    let body: unknown;
    try {
      body = await response.json();
      signal.throwIfAborted();
      return schema.parse(body);
    } catch (error) {
      signal.throwIfAborted();
      // Contract drift is a platform defect, not something to show as a schema dump.
      throw new ApiRequestError(
        response.status,
        'invalid_response',
        'Mio returned an unexpected response. Reload and try again.',
        error instanceof Error ? error.message : undefined,
      );
    }
  };
  try {
    return await Promise.race([request(), aborted]);
  } finally {
    clearTimeout(timeout);
    options.signal?.removeEventListener('abort', abortFromCaller);
    if (rejectOnAbort) signal.removeEventListener('abort', rejectOnAbort);
  }
}
